import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { io, type Socket } from "socket.io-client";
import {
  Box,
  Button,
  Loader,
  Text,
  Textarea,
  Stack,
  Group,
  Paper,
  Badge,
  ThemeIcon,
  Divider,
  useMantineTheme,
} from "@mantine/core";
import {
  IconMessage,
  IconWifiOff,
  IconAlertCircle,
  IconSend,
} from "@tabler/icons-react";
import trpc, { getToken } from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import {
  getCollaborationChatSocketPath,
  getCollaborationChatSocketUrl,
} from "@/shared/constants/collaborationChat.const";

const BRAND = "#04305c";
const CHAT_BG = "#E8F1FA";

const sendButtonStyles = (enabled: boolean) => ({
  root: {
    backgroundColor: BRAND,
    color: "#ffffff",
    opacity: enabled ? 1 : 0.6,
    transition: "opacity 120ms ease, transform 120ms ease, box-shadow 120ms ease",
  },
});

function formatMessageTime(iso: string) {
  const d = new Date(iso);
  const now = new Date();
  const sameDay =
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear();
  return sameDay
    ? d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
    : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export type ChatMessageDto = {
  id: string;
  matchId: string;
  senderUserId: string;
  body: string;
  createdAt: string;
};

type Props = {
  matchId: string;
  /** When false, socket is not opened and copy explains why. */
  chatOpen: boolean;
  collaborationReadOnly?: boolean;
  currentUserId: string | undefined;
  /** Show chat UI but keep it locked (no socket, no sending). */
  previewLocked?: boolean;
};

export default function CollaborationChatTab({
  matchId,
  chatOpen,
  collaborationReadOnly = false,
  currentUserId,
  previewLocked = false,
}: Props) {
  const theme = useMantineTheme();
  const [messages, setMessages] = useState<ChatMessageDto[]>([]);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [input, setInput] = useState("");
  const [socketConnected, setSocketConnected] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const seenIds = useRef<Set<string>>(new Set());
  const scrollRef = useRef<HTMLDivElement>(null);

  if (previewLocked) {
    return (
      <Stack gap={0} style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }} mih={360}>
        <Group justify="space-between" align="center" wrap="nowrap" px="xs" pb="sm">
          <Text size="sm" fw={600} c="dark.6">
            Messages
          </Text>
          <Badge size="sm" variant="light" color="yellow" styles={{ root: { textTransform: "none" as const } }}>
            Locked
          </Badge>
        </Group>
        <Divider />
        <Stack gap={0} style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }} pt="md">
          <Paper
            withBorder
            shadow="xs"
            radius="md"
            p="md"
            flex={1}
            style={{
              overflowY: "auto",
              minHeight: 200,
              backgroundColor: CHAT_BG,
              borderColor: theme.colors.blue[2],
            }}
          >
            <Stack align="center" justify="center" gap="sm" py="xl" mih={200}>
              <ThemeIcon size={48} radius="xl" variant="light" color="gray">
                <IconMessage size={26} stroke={1.25} />
              </ThemeIcon>
              <Text ta="center" size="sm" c="dimmed" maw={320}>
                Sending messages unlocks only after both companies complete payment.
              </Text>
            </Stack>
          </Paper>
          <Paper
            withBorder
            shadow="sm"
            radius="md"
            p="sm"
            mt="md"
            style={{
              borderColor: theme.colors.gray[2],
              backgroundColor: theme.white,
            }}
          >
            <Group align="flex-end" gap="sm" wrap="nowrap">
              <Textarea
                placeholder="Make payment to start chatting…"
                description="Chat is locked."
                descriptionProps={{ c: "dimmed", fz: 10, mb: 4 }}
                value=""
                onChange={() => { }}
                minRows={2}
                maxRows={6}
                autosize
                variant="filled"
                radius="md"
                style={{ flex: 1 }}
                disabled
                readOnly
                styles={{
                  input: {
                    backgroundColor: theme.colors.gray[0],
                    border: `1px solid ${theme.colors.gray[2]}`,
                  },
                }}
              />
              <Button
                h={44}
                px="lg"
                radius="md"
                variant="filled"
                leftSection={<IconSend size={18} />}
                disabled
                styles={sendButtonStyles(false)}
              >
                Send
              </Button>
            </Group>
          </Paper>
        </Stack>
      </Stack>
    );
  }

  const appendMessages = useCallback((incoming: ChatMessageDto[]) => {
    setMessages((prev) => {
      const merged = [...prev];
      for (const m of incoming) {
        if (seenIds.current.has(m.id)) continue;
        seenIds.current.add(m.id);
        merged.push(m);
      }
      merged.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      return merged;
    });
  }, []);

  const loadInitial = useCallback(async () => {
    if (!chatOpen) return;
    setLoadingHistory(true);
    setJoinError(null);
    try {
      const res = await trpc.collaboration.listChatMessages.query({ matchId, take: 80 });
      const normalized: ChatMessageDto[] = res.messages.map((m: { id: string; senderUserId: string; body: string; createdAt: Date | string }) => ({
        id: m.id,
        matchId,
        senderUserId: m.senderUserId,
        body: m.body,
        createdAt: m.createdAt instanceof Date ? m.createdAt.toISOString() : String(m.createdAt),
      }));
      seenIds.current = new Set(normalized.map((m) => m.id));
      setMessages(
        [...normalized].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
      );
      setNextCursor(res.nextCursor);
    } catch (e) {
      handleTrpcError(e);
    } finally {
      setLoadingHistory(false);
    }
  }, [chatOpen, matchId]);

  useEffect(() => {
    if (!chatOpen) {
      setMessages([]);
      seenIds.current.clear();
      return;
    }
    void loadInitial();
  }, [chatOpen, loadInitial]);

  useEffect(() => {
    if (!chatOpen || !currentUserId) return;

    const token = getToken();
    if (!token) {
      setJoinError("Not signed in.");
      return;
    }

    const socket = io(getCollaborationChatSocketUrl(), {
      path: getCollaborationChatSocketPath(),
      transports: ["websocket", "polling"],
      auth: { token },
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      setSocketConnected(true);
      setJoinError(null);
      socket.emit("collab_chat:join", { matchId }, (ack: { ok?: boolean; message?: string }) => {
        if (ack && ack.ok === false) {
          setJoinError(ack.message ?? "Could not join chat.");
        }
      });
    });

    socket.on("disconnect", () => {
      setSocketConnected(false);
    });

    socket.on("connect_error", (err: Error) => {
      setJoinError(err.message || "Connection failed");
    });

    socket.on("collab_chat:message", (dto: ChatMessageDto) => {
      if (dto.matchId !== matchId) return;
      appendMessages([dto]);
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
      setSocketConnected(false);
    };
  }, [appendMessages, chatOpen, currentUserId, matchId]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages.length]);

  const loadMore = useCallback(async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await trpc.collaboration.listChatMessages.query({
        matchId,
        cursor: nextCursor,
        take: 40,
      });
      setNextCursor(res.nextCursor);
      const older = res.messages.map((m: { id: string; senderUserId: string; body: string; createdAt: Date | string }) => ({
        id: m.id,
        matchId,
        senderUserId: m.senderUserId,
        body: m.body,
        createdAt: typeof m.createdAt === "string" ? m.createdAt : (m.createdAt as Date).toISOString(),
      }));
      appendMessages(older);
    } catch (e) {
      handleTrpcError(e);
    } finally {
      setLoadingMore(false);
    }
  }, [appendMessages, matchId, nextCursor, loadingMore]);

  const send = useCallback(() => {
    if (collaborationReadOnly) return;
    const text = input.trim();
    if (!text || !socketRef.current?.connected) return;
    const socket = socketRef.current;
    socket.emit(
      "collab_chat:message",
      { matchId, text },
      (ack: { ok?: boolean; message?: string; error?: string }) => {
        if (ack && ack.ok === false) {
          setJoinError(ack.message ?? ack.error ?? "Send failed");
        }
      }
    );
    setInput("");
  }, [input, matchId, collaborationReadOnly]);

  const statusBadge = useMemo(() => {
    if (!chatOpen) return null;
    if (joinError) {
      return (
        <Badge
          size="sm"
          variant="light"
          color="red"
          leftSection={<IconAlertCircle size={12} />}
          styles={{ root: { textTransform: "none" as const } }}
        >
          {joinError}
        </Badge>
      );
    }
    if (!socketConnected) {
      return (
        <Badge
          size="sm"
          variant="light"
          color="yellow"
          leftSection={<IconWifiOff size={12} />}
          styles={{ root: { textTransform: "none" as const } }}
        >
          Connecting…
        </Badge>
      );
    }
    return null;
  }, [chatOpen, joinError, socketConnected]);

  if (!chatOpen) {
    return (
      <Box py="md">
        <Text c="dimmed">
          Direct chat between you and your partner unlocks after both companies have completed the collaboration
          payment.
        </Text>
      </Box>
    );
  }

  const canSend = Boolean(input.trim() && socketConnected && !collaborationReadOnly);

  return (
    <Stack
      gap={0}
      style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
      mih={360}
    >
      <Group justify="space-between" align="center" wrap="nowrap" px="xs" pb="sm">
        <Text size="sm" fw={600} c="dark.6">
          Messages
        </Text>
        <Group gap="xs" wrap="wrap" justify="flex-end">
          {collaborationReadOnly ? (
            <Badge size="sm" variant="light" color="gray" styles={{ root: { textTransform: "none" as const } }}>
              Read-only — active period ended
            </Badge>
          ) : null}
          {statusBadge}
        </Group>
      </Group>
      <Divider />
      {loadingHistory ? (
        <CenteredLoader />
      ) : (
        <Stack gap={0} style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }} pt="md">
          {nextCursor ? (
            <Box pb="sm" style={{ display: "flex", justifyContent: "center" }}>
              <Button variant="light" color="gray" size="xs" radius="xl" onClick={() => void loadMore()} loading={loadingMore}>
                Load older messages
              </Button>
            </Box>
          ) : null}
          <Paper
            ref={scrollRef}
            withBorder
            shadow="xs"
            radius="md"
            p="md"
            flex={1}
            style={{
              overflowY: "auto",
              minHeight: 200,
              backgroundColor: CHAT_BG,
              borderColor: theme.colors.blue[2],
            }}
          >
            {messages.length === 0 ? (
              <Stack align="center" justify="center" gap="sm" py="xl" mih={200}>
                <ThemeIcon size={48} radius="xl" variant="light" color="gray">
                  <IconMessage size={26} stroke={1.25} />
                </ThemeIcon>
                <Text ta="center" size="sm" c="dimmed" maw={280}>
                  No messages yet. Say hello to your partner—messages appear here in real time.
                </Text>
              </Stack>
            ) : (
              <Stack gap="lg" pr={4}>
                {messages.map((m) => {
                  const mine = m.senderUserId === currentUserId;
                  return (
                    <Box
                      key={m.id}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: mine ? "flex-end" : "flex-start",
                        maxWidth: "100%",
                      }}
                    >
                      <Box
                        maw="min(520px, 88%)"
                        miw={0}
                        px="md"
                        py={10}
                        style={{
                          borderRadius: mine
                            ? `${theme.radius.lg}px ${theme.radius.xs}px ${theme.radius.lg}px ${theme.radius.lg}px`
                            : `${theme.radius.xs}px ${theme.radius.lg}px ${theme.radius.lg}px ${theme.radius.lg}px`,
                          backgroundColor: mine ? BRAND : theme.white,
                          color: mine ? theme.white : theme.colors.dark[8],
                          boxShadow: mine ? "0 1px 8px rgba(4, 48, 92, 0.28)" : theme.shadows.xs,
                          border: mine ? "none" : `1px solid ${theme.colors.gray[2]}`,
                        }}
                      >
                        <Text
                          size="sm"
                          lh={1.55}
                          c={mine ? "#f8fafc" : "dark.8"}
                          style={{
                            whiteSpace: "pre-wrap",
                            overflowWrap: "anywhere",
                            wordBreak: "break-word",
                          }}
                        >
                          {m.body}
                        </Text>
                      </Box>
                      <Text size="xs" c="dimmed" mt={6} px={4}>
                        {formatMessageTime(m.createdAt)}
                      </Text>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Paper>
          <Paper
            withBorder
            shadow="sm"
            radius="md"
            p="sm"
            mt="md"
            style={{
              borderColor: theme.colors.gray[2],
              backgroundColor: theme.white,
            }}
          >
            <Group align="flex-end" gap="sm" wrap="nowrap">
              <Textarea
                placeholder={
                  collaborationReadOnly
                    ? "Messaging is closed for this collaboration."
                    : "Write a message…"
                }
                description={
                  collaborationReadOnly
                    ? "You can still read the history above."
                    : "Enter to send · Shift+Enter for new line"
                }
                descriptionProps={{ c: "dimmed", fz: 10, mb: 4 }}
                value={input}
                onChange={(e) => setInput(e.currentTarget.value)}
                minRows={2}
                maxRows={6}
                autosize
                variant="filled"
                radius="md"
                style={{ flex: 1 }}
                disabled={collaborationReadOnly}
                readOnly={collaborationReadOnly}
                styles={{
                  input: {
                    backgroundColor: theme.colors.gray[0],
                    border: `1px solid ${theme.colors.gray[2]}`,
                  },
                }}
                onKeyDown={(e) => {
                  if (collaborationReadOnly) return;
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
              />
              <Button
                h={44}
                px="lg"
                radius="md"
                variant="filled"
                leftSection={<IconSend size={18} />}
                onClick={send}
                disabled={!canSend}
                styles={sendButtonStyles(canSend)}
              >
                Send
              </Button>
            </Group>
          </Paper>
        </Stack>
      )}
    </Stack>
  );
}

function CenteredLoader() {
  return (
    <Box py="xl">
      <Loader />
    </Box>
  );
}
